/* ─────────────────────────────────────────────────────────
   Grant OS — Design Token Constants (JS mirror of tokens.css)
   Use these in React inline styles.
   CSS custom properties are the canonical source in tokens.css.
───────────────────────────────────────────────────────── */

export const TOKENS = {
  /* Backgrounds */
  bgApp:      "#050A14",
  bgCard:     "#080F1E",
  bgElevated: "#0D1526",
  bgHeader:   "#050A14",

  /* Accent — Electric Indigo */
  gold:      "#5B4FE9",
  slateBlue: "#7C73F0",

  /* Borders */
  border:      "#1A2340",
  borderLight: "#232E4A",

  /* Status */
  statusGreen: "#22D4A8",
  statusAmber: "#F5A623",
  statusRed:   "#E84545",
  statusBlue:  "#3B82F6",

  /* Text hierarchy */
  textPrimary:   "#F0EFFF",
  textSecondary: "#B8B5D4",
  textMuted:     "#6B68A8",
  textDisabled:  "#3D3A70",

  /* Typography */
  fontHeading: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontUI:      "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono:    "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",

  /* Type scale */
  textXs:   "12px",
  textSm:   "14px",
  textBase: "16px",
  textLg:   "18px",
  textXl:   "24px",
  text2xl:  "32px",
  text3xl:  "40px",

  /* Line heights */
  leadingTight:   1.2,
  leadingNormal:  1.5,
  leadingRelaxed: 1.7,

  /* Spacing */
  space1:  "4px",
  space2:  "8px",
  space3:  "12px",
  space4:  "16px",
  space6:  "24px",
  space8:  "32px",
  space12: "48px",
  space16: "64px",

  /* Border radius */
  radiusNone: "0px",
  radiusSm:   "4px",
  radiusMd:   "8px",
  radiusLg:   "12px",
  radiusFull: "9999px",

  /* Shadows */
  shadowCard:     "0 1px 3px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.35)",
  shadowElevated: "0 8px 24px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35)",
  shadowGoldGlow: "0 0 0 3px rgba(91,79,233,0.25)",
};

/* ── Status badge style objects ─────────────────────────── */
export const BADGE = {
  green:  { background: "rgba(34,212,168,0.12)",  color: "#22D4A8", border: "1px solid rgba(34,212,168,0.25)"  },
  amber:  { background: "rgba(245,166,35,0.12)",  color: "#F5A623", border: "1px solid rgba(245,166,35,0.25)"  },
  red:    { background: "rgba(232,69,69,0.12)",   color: "#E84545", border: "1px solid rgba(232,69,69,0.25)"   },
  blue:   { background: "rgba(59,130,246,0.12)",  color: "#3B82F6", border: "1px solid rgba(59,130,246,0.25)"  },
  gold:   { background: "rgba(91,79,233,0.15)",   color: "#5B4FE9", border: "1px solid rgba(91,79,233,0.3)"    },
  muted:  { background: "rgba(107,104,168,0.12)", color: "#6B68A8", border: "1px solid rgba(107,104,168,0.25)" },
};

/* Badge base style (apply spread + one of BADGE.* for color) */
export const BADGE_BASE = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 10px",
  borderRadius: "9999px",
  fontSize: "12px",
  fontFamily: "'Inter', system-ui, sans-serif",
  fontWeight: 500,
  letterSpacing: "0.02em",
  whiteSpace: "nowrap",
};

/* ── Application status → badge color map ───────────────── */
export const STATUS_BADGE = {
  "Awarded":           BADGE.green,
  "Pending":           BADGE.amber,
  "Not Awarded":       BADGE.red,
  "Not Submitted":     BADGE.muted,
  "Ongoing Application": BADGE.blue,
  "Submitted":         BADGE.blue,
  "Active":            BADGE.green,
  "Completed":         BADGE.gold,
  "Terminated":        BADGE.red,
};

/* ── Document surface (light) — extraction result card ───
   The signature element: serif document content on a light
   surface against the dark operational app. */
export const DOC_TOKENS = {
  bg:        "#FAFAF9",
  surface:   "#FFFFFF",
  border:    "#E5E3DC",
  borderDim: "#EFEDE7",
  text:      "#211F1A",
  textSub:   "#57544A",
  textMuted: "#8A867A",
  accent:    "#5B4FE9",
  success:   "#0E7A5F",
  warning:   "#9A6700",
  danger:    "#B42318",
};

/* ── Skeleton loading colors (dark surfaces) ─────────────── */
export const SKELETON = {
  base: "#0D1526",
  highlight: "#1A2340",
};

/* ── Sector chips — FUNCTIONAL color-coding (design brief §7)
   Applied only via the dim pattern (~15% bg, colored text,
   dim border, mono label). Accent #5B4FE9 stays reserved for
   interaction — never categorization. Unlisted sectors fall
   back to textMuted. */
const SECTOR_PALETTE = {
  Health:       "#E8628C",
  Education:    "#4FA3E8",
  Agriculture:  "#A3C255",
  Climate:      "#38B6D4",
  Environment:  "#38B6D4",
  Humanitarian: "#E87B5B",
};

export function sectorChipStyle(sector) {
  const hex = SECTOR_PALETTE[sector] || TOKENS.textMuted;
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return {
    background: `rgba(${r},${g},${b},0.15)`,
    color: hex,
    border: `1px solid rgba(${r},${g},${b},0.28)`,
    fontFamily: TOKENS.fontMono,
  };
}

/* ── Sidebar palette (always dark, sidebar-specific) ────── */
export const SB_TOKENS = {
  bg:        "#050A14",
  border:    "#1A2340",
  text:      "#F0EFFF",
  textMuted: "#6B68A8",
  hover:     "#080F1E",
  active:    "#0D1526",
};

/* ── Operational palette (always dark — dashboard & charts) */
export const OP_TOKENS = {
  pageBg:       "#050A14",
  sectionBg:    "#080F1E",
  cardBg:       "#0A1220",
  cardBorder:   "#1A2340",
  text:         "#F0EFFF",
  textSub:      "#6B68A8",
  textMuted:    "#3D3A70",
  inputBg:      "#080F1E",
  inputBorder:  "#1A2340",
  gridLine:     "#1A2340",
  axisText:     "#6B68A8",
  tooltipBg:    "#0D1526",
  tooltipBorder:"#232E4A",
  btnPrimary:        "#5B4FE9",
  btnPrimaryText:    "#F0EFFF",
  btnSecBorder:      "#1A2340",
  btnSecText:        "#6B68A8",
  btnDangerBorder:   "#3f1f1f",
  btnDangerText:     "#E84545",
};
